import type { ReactNode } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import { CaseIcon, ChatIcon, DocumentIcon } from '../../ui/portal-icons';
import type { TranslationKey } from '../../core/i18n/translation';

const POINTS: readonly { icon: ReactNode; label: TranslationKey }[] = [
    { icon: <CaseIcon />, label: 'auth.aside.point1' },
    { icon: <DocumentIcon />, label: 'auth.aside.point2' },
    { icon: <ChatIcon />, label: 'auth.aside.point3' }
];

/**
 * The page the three account screens are drawn on: sign-in, registration and activation.
 *
 * **The panel beside the form is the answer to "what am I signing in to".** These screens used to
 * be a form floating in the middle of a white page with no indication of what was behind it, which
 * for a portal nobody visits daily is a request for a password with no reason attached. The three
 * points are what the portal actually does, and they are the same three whichever of the screens
 * somebody arrived at.
 *
 * It is hidden below `lg` rather than stacked. On a telephone the form is the whole screen and
 * anything above it is something to scroll past to reach the box — and the customer opening this on
 * a telephone has usually come from an invitation and already knows why.
 *
 * The card paints its own ground rather than letting the chrome's show through, because a form is
 * the one thing on this site that has to look like a surface you can type on.
 */
export function AuthSurface({ children }: { children: ReactNode }) {
    const { t } = useTranslation();

    return (
        <div className="mx-auto grid max-w-5xl items-center gap-10 px-4 py-12 lg:grid-cols-2 lg:py-20">
            <div className="w-full max-w-md justify-self-center rounded-3xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8 dark:border-slate-800 dark:bg-slate-900">
                {children}
            </div>

            <aside className="hidden lg:block">
                <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100">
                    {t('auth.aside.title')}
                </h2>

                <ul className="mt-6 space-y-4">
                    {POINTS.map((point) => (
                        <li key={point.label} className="flex items-start gap-3">
                            <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
                                {point.icon}
                            </span>
                            <span className="pt-1.5 text-slate-700 dark:text-slate-300">{t(point.label)}</span>
                        </li>
                    ))}
                </ul>
            </aside>
        </div>
    );
}
